import { designTokens } from "@itandi/itandi-bb-ui";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Form } from "@itandi/itandi-bb-ui/Form";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useCallback, useId, useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import type { AdminCompanyStoreSettingEdit } from "~/models/CompanyStoreSetting";
import { update } from "~/utils/api/admin/CompanyStoreSetting";
import { isSuccess } from "~/utils/api/api_handler";
import { generatePath } from "~/utils/generatePath";

type Props = {
	companyStoreSetting: AdminCompanyStoreSettingEdit;
};

export function CompanyStoreSettingForm({ companyStoreSetting }: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const handleOpenConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(true);
	}, []);
	const handleCloseConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(false);
	}, []);
	const { handleSubmit, register } = useForm<AdminCompanyStoreSettingEdit>({
		defaultValues: {
			robotPaymentStoreCode: companyStoreSetting.robotPaymentStoreCode
		},
		mode: "onBlur"
	});

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/itandi_account_sessions/current_account"),
		async (_key, { arg }: { arg: AdminCompanyStoreSettingEdit }) => update(arg)
	);

	const handleAccept = useCallback(
		async (submitValue: AdminCompanyStoreSettingEdit) => {
			const response = await trigger(submitValue);
			if (isSuccess(response)) {
				notify({ content: "保存しました。", variant: "success" });
				handleCloseConfirmModal();
				return;
			}
			notify({ content: "保存に失敗しました。", variant: "error" });
		},
		[handleCloseConfirmModal, notify, trigger]
	);

	return (
		<>
			<Form id={formId} onSubmit={handleSubmit(handleOpenConfirmModal)}>
				<FormGroup maxW="32rem" variant="vertical">
					<FormControl label="請求管理ロボ用店舗コード">
						<Input {...register("robotPaymentStoreCode")} />
					</FormControl>
				</FormGroup>
			</Form>
			<Buttonv2
				form={formId}
				marginTop={designTokens["spacing/m"]}
				type="submit"
				variant="primary"
			>
				保存
			</Buttonv2>
			{isConfirmModalOpen ? (
				<Modalv2
					isOpen
					onClose={isMutating ? () => undefined : handleCloseConfirmModal}
					size="s"
				>
					<ModalHeader title="設定の保存" />
					<ModalBody>
						<Paragraph>設定を保存します。</Paragraph>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Buttonv2
								disabled={isMutating}
								onClick={handleCloseConfirmModal}
								variant="secondary"
							>
								キャンセル
							</Buttonv2>
						}
						rightArea={
							<Buttonv2
								loading={isMutating}
								onClick={handleSubmit(handleAccept)}
								variant="primary"
							>
								保存
							</Buttonv2>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
